import { useEffect, MutableRefObject } from 'react'
import { User } from '@weblite/ts-action/user'
import { nothing } from '$func/utils'
import {
  resetEditor,
  insertContent,
  convertHtmlToFormattedText,
  insertMentionIntoEditor,
  putCaretAtEndOfEditor,
} from './ops'

interface EditorAPI {
  insertText: (text: string | string[]) => void
  insertMention: (user: User) => void

  focus: () => void
  blur: () => void
  setText: (formattedText: string) => void
  getText: () => string

  setKeepFocus: (value: boolean) => void
  setHasFocus: (value: boolean) => void

  keepFocus: boolean
  hasFocus: boolean
}

let apiValues: EditorAPI = {
  insertText: nothing,
  insertMention: nothing,

  focus: nothing,
  blur: nothing,
  setText: nothing,
  getText: () => null,

  setKeepFocus: nothing,
  setHasFocus: nothing,
  keepFocus: false,
  hasFocus: false,
}

export const editorAPI: EditorAPI = {
  insertText: text => apiValues.insertText(text),
  insertMention: user => apiValues.insertMention(user),
  focus: () => apiValues.focus(),
  blur: () => apiValues.blur(),
  setText: text => apiValues.setText(text),
  getText: () => apiValues.getText(),

  setKeepFocus(value: boolean) {
    apiValues.keepFocus = value
  },

  setHasFocus(value: boolean) {
    apiValues.hasFocus = value
  },

  get keepFocus() {
    return apiValues.keepFocus
  },

  get hasFocus() {
    return apiValues.hasFocus
  },
}

function initiateEditorAPI(api: Partial<EditorAPI>) {
  apiValues = { ...apiValues, ...api }
  // @ts-ignore
  window.editor = apiValues
}

export function useEditorAPI(
  EditorRef: MutableRefObject<HTMLDivElement>,
  updateEditorState: (contentChanged?: boolean) => void,
) {
  useEffect(() => {
    if (!EditorRef.current) return
    EditorRef.current.addEventListener('paste', () => {
      EditorRef.current.blur()
      EditorRef.current.focus()
    })
  }, [])
  useEffect(() => {
    initiateEditorAPI({
      insertText(text: string) {
        if (!EditorRef.current) return
        insertContent(EditorRef.current, text)
        updateEditorState()

        EditorRef.current.focus()
      },

      insertMention(user: User) {
        if (!EditorRef.current) return

        insertMentionIntoEditor(EditorRef.current, user)
        updateEditorState()
      },

      focus() {
        if (!EditorRef.current) return
        EditorRef.current.focus()
      },

      blur() {
        if (!EditorRef.current) return
        EditorRef.current.blur()
      },

      setText(text: string) {
        if (!EditorRef.current) return
        putCaretAtEndOfEditor(EditorRef.current)
        resetEditor(EditorRef.current)
        insertContent(EditorRef.current, text)
        EditorRef.current.scrollTo?.(0, EditorRef.current.scrollHeight)
        EditorRef.current.focus()
        updateEditorState()
      },

      getText() {
        if (!EditorRef.current) return ''
        return convertHtmlToFormattedText(EditorRef.current)
      },
    })
  }, [updateEditorState])
}
